import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { USessionService } from './services/usession.service';

/**
 * Stamps `uSession` onto the payloads of the signup journey APIs.
 *
 * Done here rather than at each call site because these fire from three signup
 * components, two services and a shared package, and `paymentdetailsapi` is not
 * even referenced from this repo. One rule in one place covers them all and
 * cannot drift as call sites are added.
 *
 * Only the endpoints listed below are touched — this does not blanket every
 * outgoing request.
 *
 * NOTE: the analytics call in the signup components uses `fetch`, not
 * HttpClient, so interceptors never see it. Those add the field directly.
 */

/**
 * URL fragments identifying the five journey APIs. Matched as substrings so the
 * v3/v5/generic/draft variants of each are all covered:
 *
 *   api/customer/v5/validation, api/corporate/customer/generic/v5/validation, …
 *   api/customer/v5/registration, api/corporate/customer/v3/registration, …
 *   api/common/customer/transaction
 *   api/customer/subscription, api/corporate/customer/subscription
 *   api/common/analytics
 */
const USESSION_URL_PATTERNS = [
  '/validation',
  '/registration',
  '/customer/transaction',
  '/subscription',
  '/analytics',
];

@Injectable()
export class USessionInterceptor implements HttpInterceptor {
  constructor(private uSession: USessionService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.uSession.enabled) return next.handle(req);
    if (req.method !== 'POST' && req.method !== 'PUT') return next.handle(req);
    if (!this.matches(req.url)) return next.handle(req);

    // Only plain JSON objects. FormData (void cheque uploads) and string bodies
    // are left exactly as they are.
    const body = req.body;
    if (!body || typeof body !== 'object' || Array.isArray(body)) return next.handle(req);
    if (typeof FormData !== 'undefined' && body instanceof FormData) return next.handle(req);
    if (body[this.uSession.fieldName]) return next.handle(req);

    return next.handle(req.clone({ body: this.uSession.attach(body) }));
  }

  private matches(url: string): boolean {
    if (!url) return false;
    // Ignore the query string so a match cannot come from a parameter value.
    const path = url.split('?')[0].toLowerCase();
    return USESSION_URL_PATTERNS.some((pattern) => path.includes(pattern));
  }
}
